import { randomUUID } from '../uuid.js';
import { CHANCE_CARDS, COMMUNITY_CARDS } from '../data/cards.js';
import { PLAYER_COLORS, PROPERTY_TYPES, TOKENS } from '../data/board.js';
import { Player } from '../models/Player.js';
import { invariant } from './GameError.js';
import {
  assertBuildAllowed,
  assertSellBuildingAllowed,
  calculateRent,
  groupHasBuildings,
  liquidationCapacity,
} from './rules.js';

const ACTIVE_PHASES_FOR_MANAGEMENT = new Set(['awaiting_roll', 'awaiting_end', 'awaiting_debt']);

function uniqueIds(values = []) {
  return [...new Set(values.map(Number).filter(Number.isInteger))];
}

export class GameEngine {
  constructor(game, { random = Math.random, logger = console, PlayerClass = Player } = {}) {
    this.game = game;
    this.random = random;
    this.logger = logger;
    this.PlayerClass = PlayerClass;
  }

  rollDie() {
    return Math.floor(this.random() * 6) + 1;
  }

  addPlayer({ name, token, isBot = false }) {
    const { game } = this;
    invariant(game.status === 'waiting', 'GAME_ALREADY_STARTED', 'Матч уже начался.');
    invariant(game.players.length < game.settings.maxPlayers, 'ROOM_FULL', 'В комнате нет свободных мест.');
    invariant(TOKENS.includes(token), 'INVALID_TOKEN', 'Выберите доступную фишку.');
    invariant(!game.players.some((player) => player.token === token), 'TOKEN_TAKEN', 'Эту фишку уже выбрал другой игрок.');
    invariant(!game.players.some((player) => player.name.toLocaleLowerCase('ru') === name.toLocaleLowerCase('ru')), 'NAME_TAKEN', 'Игрок с таким именем уже в комнате.');

    const player = new this.PlayerClass({
      name,
      token,
      color: PLAYER_COLORS.find((color) => !game.players.some((player) => player.color === color)),
      isBot,
    });
    player.money = game.settings.startingMoney;
    game.players.push(player);
    game.addLog(`В комнате новый игрок: ${player.name}.`, 'connection');
    return player;
  }

  setBotCount(count) {
    const { game } = this;
    invariant(game.status === 'waiting', 'GAME_ALREADY_STARTED', 'Состав можно менять только до старта.');
    const humans = game.players.filter((player) => !player.isBot);
    invariant(Number.isInteger(count) && count >= 0 && count + humans.length <= game.settings.maxPlayers, 'INVALID_BOT_COUNT', 'Всего должно быть не больше четырёх участников.');
    while (game.players.filter((player) => player.isBot).length > count) {
      const index = game.players.findLastIndex((player) => player.isBot);
      const [removed] = game.players.splice(index, 1);
      game.addLog(`${removed.name} удалён из лобби.`, 'connection');
    }
    while (game.players.filter((player) => player.isBot).length < count) {
      let suffix = 0;
      const names = ['Bot Alpha', 'Bot Bravo', 'Bot Charlie', 'Bot Delta'];
      while (game.players.some((player) => player.name.toLowerCase() === (names[suffix] ?? `Bot ${suffix + 1}`).toLowerCase())) suffix += 1;
      this.addPlayer({ name: names[suffix] ?? `Bot ${suffix + 1}`, token: TOKENS.find((token) => !game.players.some((player) => player.token === token)), isBot: true });
    }
    return game;
  }

  start() {
    const { game } = this;
    invariant(game.status === 'waiting', 'GAME_ALREADY_STARTED', 'Матч уже начался.');
    invariant(game.players.length >= 2, 'NOT_ENOUGH_PLAYERS', 'Для старта нужны минимум два игрока.');
    invariant(game.players.every((player) => player.connected), 'PLAYER_OFFLINE', 'Дождитесь подключения всех игроков.');

    const openingRolls = game.players.map((player, index) => ({
      index,
      player,
      total: this.rollDie() + this.rollDie(),
    }));
    openingRolls.forEach(({ player, total }) => game.addLog(`${player.name}: бросок ${total} для определения очереди.`));
    openingRolls.sort((a, b) => b.total - a.total || a.index - b.index);

    game.currentPlayerIndex = openingRolls[0].index;
    game.status = 'active';
    game.phase = 'awaiting_roll';
    game.doublesCount = 0;
    game.extraTurn = false;
    game.addLog(`Игра началась. Первым ходит ${game.currentPlayer.name}.`, 'system');
    return game;
  }

  rollDice(playerId) {
    const { game } = this;
    const player = this.assertCurrentPlayer(playerId);
    invariant(game.phase === 'awaiting_roll', 'ROLL_NOT_ALLOWED', 'Сейчас нельзя бросать кубики.');
    invariant(!game.pendingDebt && !game.pendingPurchase, 'ACTION_REQUIRED', 'Сначала завершите обязательное действие.');

    const values = [this.rollDie(), this.rollDie()];
    const isDouble = values[0] === values[1];
    game.dice = {
      values,
      total: values[0] + values[1],
      isDouble,
      sequence: game.dice.sequence + 1,
    };
    game.lastMove = {
      sequence: game.dice.sequence,
      playerId: player.id,
      from: player.position,
      path: [],
      to: player.position,
    };
    game.addLog(`${player.name} бросает кубики: ${game.dice.total}${isDouble ? ' — дубль!' : '.'}`, 'dice');

    if (player.jail.inJail) {
      return this.resolveJailRoll(player, isDouble);
    }

    game.doublesCount = isDouble ? game.doublesCount + 1 : 0;
    game.extraTurn = isDouble;
    if (game.doublesCount >= 3) {
      game.addLog(`${player.name}: три дубля подряд — отправление в тюрьму.`, 'jail');
      this.sendToJail(player);
      game.extraTurn = false;
      game.phase = 'awaiting_end';
      return game;
    }

    this.moveBy(player, game.dice.total, true);
    this.resolveLanding(player);
    return game;
  }

  resolveJailRoll(player, isDouble) {
    const { game } = this;
    game.extraTurn = false;
    game.doublesCount = 0;
    if (isDouble) {
      player.jail = { inJail: false, turns: 0 };
      game.addLog(`${player.name} выбрасывает дубль и выходит из тюрьмы.`, 'jail');
      this.moveBy(player, game.dice.total, false);
      this.resolveLanding(player);
      return game;
    }

    player.jail.turns += 1;
    if (player.jail.turns < 3) {
      game.addLog(`${player.name} остаётся в тюрьме (${player.jail.turns}/3).`, 'jail');
      game.phase = 'awaiting_end';
      return game;
    }

    const paid = this.requestPayment(player, null, game.settings.jailFine, 'обязательный штраф за освобождение', {
      type: 'jailMove',
      steps: game.dice.total,
    });
    if (paid) {
      player.jail = { inJail: false, turns: 0 };
      game.addLog(`${player.name} платит штраф и покидает тюрьму.`, 'jail');
      this.moveBy(player, game.dice.total, false);
      this.resolveLanding(player);
    }
    return game;
  }

  payJailFine(playerId) {
    const { game } = this;
    const player = this.assertCurrentPlayer(playerId);
    invariant(game.phase === 'awaiting_roll' && player.jail.inJail, 'NOT_IN_JAIL', 'Этот игрок сейчас не в тюрьме.');
    const paid = this.requestPayment(player, null, game.settings.jailFine, 'штраф за освобождение', { type: 'jailRelease' });
    if (paid) {
      player.jail = { inJail: false, turns: 0 };
      game.phase = 'awaiting_roll';
      game.addLog(`${player.name} платит ${game.settings.jailFine} и выходит из тюрьмы.`, 'jail');
    }
    return game;
  }

  useJailCard(playerId) {
    const { game } = this;
    const player = this.assertCurrentPlayer(playerId);
    invariant(game.phase === 'awaiting_roll' && player.jail.inJail, 'NOT_IN_JAIL', 'Этот игрок сейчас не в тюрьме.');
    invariant(player.jailFreeCards > 0, 'NO_JAIL_CARD', 'У вас нет карточки освобождения.');
    player.jailFreeCards -= 1;
    player.jail = { inJail: false, turns: 0 };
    game.addLog(`${player.name} использует карточку освобождения.`, 'jail');
    return game;
  }

  buyProperty(playerId) {
    const { game } = this;
    const player = this.assertCurrentPlayer(playerId);
    const pending = game.pendingPurchase;
    invariant(game.phase === 'awaiting_purchase' && pending?.playerId === playerId, 'NO_PENDING_PURCHASE', 'Сейчас нечего покупать.');
    const property = game.getProperty(pending.propertyId);
    const cell = game.board[pending.propertyId];
    invariant(property && !property.ownerId, 'PROPERTY_UNAVAILABLE', 'Объект уже занят.');
    invariant(player.money >= cell.price, 'NOT_ENOUGH_MONEY', 'Недостаточно денег для покупки.');

    player.money -= cell.price;
    player.properties.push(property.id);
    property.ownerId = player.id;
    game.pendingPurchase = null;
    game.addLog(`${player.name} покупает «${cell.name}» за ${cell.price}.`, 'purchase');
    this.setPostLandingPhase(player);
    return game;
  }

  declineProperty(playerId) {
    const { game } = this;
    const player = this.assertCurrentPlayer(playerId);
    const pending = game.pendingPurchase;
    invariant(game.phase === 'awaiting_purchase' && pending?.playerId === playerId, 'NO_PENDING_PURCHASE', 'Сейчас нечего отклонять.');
    const cell = game.board[pending.propertyId];
    game.pendingPurchase = null;
    game.addLog(`${player.name} отказывается покупать «${cell.name}».`);
    this.setPostLandingPhase(player);
    return game;
  }

  endTurn(playerId) {
    const { game } = this;
    this.assertCurrentPlayer(playerId);
    invariant(game.phase === 'awaiting_end', 'END_TURN_NOT_ALLOWED', 'Сейчас ход нельзя завершить.');
    invariant(!game.pendingDebt && !game.pendingPurchase, 'ACTION_REQUIRED', 'Сначала завершите обязательное действие.');
    this.advanceTurn();
    return game;
  }

  build(playerId, propertyId) {
    const { game } = this;
    this.assertManagementTurn(playerId, false);
    const { property, cell, player } = assertBuildAllowed(game, playerId, Number(propertyId));
    player.money -= cell.buildCost;
    property.houses += 1;
    game.addLog(`${player.name} ${property.houses === 5 ? 'строит отель' : 'строит дом'} на «${cell.name}» за ${cell.buildCost}.`, 'building');
    return game;
  }

  sellBuilding(playerId, propertyId) {
    const { game } = this;
    this.assertManagementTurn(playerId, true);
    const { property, cell, player } = assertSellBuildingAllowed(game, playerId, Number(propertyId));
    const wasHotel = property.houses === 5;
    property.houses -= 1;
    const refund = Math.floor(cell.buildCost / 2);
    player.money += refund;
    game.addLog(`${player.name} продаёт ${wasHotel ? 'отель' : 'дом'} на «${cell.name}» за ${refund}.`, 'building');
    return game;
  }

  mortgage(playerId, propertyId) {
    const { game } = this;
    this.assertManagementTurn(playerId, true);
    const property = game.getProperty(propertyId);
    const cell = game.board[propertyId];
    invariant(property && property.ownerId === playerId, 'NOT_OWNER', 'Этот объект вам не принадлежит.');
    invariant(!property.mortgaged, 'ALREADY_MORTGAGED', 'Объект уже заложен.');
    if (cell.type === 'street') {
      invariant(!groupHasBuildings(game, cell.group), 'GROUP_HAS_BUILDINGS', 'Сначала продайте все постройки цветовой группы.');
    }
    const player = game.getPlayer(playerId);
    property.mortgaged = true;
    player.money += cell.mortgageValue;
    game.addLog(`${player.name} закладывает «${cell.name}» и получает ${cell.mortgageValue}.`, 'mortgage');
    return game;
  }

  unmortgage(playerId, propertyId) {
    const { game } = this;
    this.assertManagementTurn(playerId, false);
    const property = game.getProperty(propertyId);
    const cell = game.board[propertyId];
    invariant(property && property.ownerId === playerId, 'NOT_OWNER', 'Этот объект вам не принадлежит.');
    invariant(property.mortgaged, 'NOT_MORTGAGED', 'Объект не заложен.');
    const cost = Math.ceil(cell.mortgageValue * 1.1);
    const player = game.getPlayer(playerId);
    invariant(player.money >= cost, 'NOT_ENOUGH_MONEY', 'Недостаточно денег для выкупа.');
    player.money -= cost;
    property.mortgaged = false;
    game.addLog(`${player.name} выкупает «${cell.name}» за ${cost}.`, 'mortgage');
    return game;
  }

  resolveDebt(playerId) {
    const { game } = this;
    const debt = game.pendingDebt;
    invariant(debt?.debtorId === playerId, 'NO_PENDING_DEBT', 'У этого игрока нет ожидающего платежа.');
    const debtor = game.getPlayer(playerId);
    invariant(debtor.money >= debt.amount, 'DEBT_NOT_COVERED', 'Продайте постройки или заложите имущество, чтобы покрыть долг.');
    const creditor = debt.creditorId ? game.getPlayer(debt.creditorId) : null;
    debtor.money -= debt.amount;
    if (creditor) creditor.money += debt.amount;
    game.addLog(`${debtor.name} выплачивает ${debt.amount}${creditor ? ` игроку ${creditor.name}` : ' банку'} (${debt.reason}).`, 'payment');
    const afterPayment = debt.afterPayment;
    game.pendingDebt = null;
    this.runAfterPayment(debtor, afterPayment);
    return game;
  }

  declareBankruptcy(playerId) {
    const { game } = this;
    const debt = game.pendingDebt;
    invariant(debt?.debtorId === playerId, 'NO_PENDING_DEBT', 'Банкротство возможно только при непогашенном обязательном платеже.');
    invariant(liquidationCapacity(game, playerId) < debt.amount, 'CAN_COVER_DEBT', 'Вы ещё можете покрыть долг продажей построек и ипотекой.');
    const player = game.getPlayer(playerId);
    const creditor = debt.creditorId ? game.getPlayer(debt.creditorId) : null;

    for (const propertyId of [...player.properties]) {
      const property = game.getProperty(propertyId);
      const cell = game.board[propertyId];
      if (property.houses > 0 && cell.type === 'street') {
        player.money += Math.floor((property.houses * cell.buildCost) / 2);
        property.houses = 0;
      }
      if (creditor) {
        property.ownerId = creditor.id;
        creditor.properties.push(propertyId);
      } else {
        property.reset();
      }
    }
    player.properties = [];
    if (creditor) creditor.money += player.money;
    player.money = 0;
    player.bankrupt = true;
    game.pendingDebt = null;
    game.trades.forEach((trade) => {
      if ([trade.fromPlayerId, trade.toPlayerId].includes(playerId)) trade.status = 'cancelled';
    });
    game.addLog(`${player.name} объявляет банкротство.`, 'bankruptcy');
    this.checkWinner();
    if (game.status === 'active') {
      if (debt.afterPayment?.type === 'collectEach') this.runAfterPayment(player, debt.afterPayment);
      else this.advanceTurn();
    }
    return game;
  }

  createTrade(playerId, proposal) {
    const { game } = this;
    invariant(game.status === 'active', 'GAME_NOT_ACTIVE', 'Сделки доступны только во время матча.');
    const from = game.getPlayer(playerId);
    const to = game.getPlayer(proposal.toPlayerId);
    invariant(from && !from.bankrupt && to && !to.bankrupt && from.id !== to.id, 'INVALID_TRADE_PLAYER', 'Выберите другого активного игрока.');
    const offeredMoney = Number(proposal.offeredMoney ?? 0);
    const requestedMoney = Number(proposal.requestedMoney ?? 0);
    invariant(Number.isSafeInteger(offeredMoney) && offeredMoney >= 0 && Number.isSafeInteger(requestedMoney) && requestedMoney >= 0, 'INVALID_TRADE_MONEY', 'Сумма сделки должна быть целым неотрицательным числом.');
    const offeredPropertyIds = uniqueIds(proposal.offeredPropertyIds);
    const requestedPropertyIds = uniqueIds(proposal.requestedPropertyIds);
    this.assertTradeAssets(from, offeredMoney, offeredPropertyIds, { tradeId: 'creating', senderId: from.id, receiverId: to.id });
    this.assertTradeAssets(to, requestedMoney, requestedPropertyIds, { tradeId: 'creating', senderId: from.id, receiverId: to.id });
    invariant(offeredMoney + requestedMoney + offeredPropertyIds.length + requestedPropertyIds.length > 0, 'EMPTY_TRADE', 'Добавьте деньги или имущество в сделку.');

    game.trades = game.trades.filter((candidate) => {
      if (candidate.status === 'pending' && candidate.fromPlayerId === from.id) {
        candidate.status = 'cancelled';
        return false;
      }
      return candidate.status === 'pending';
    });

    const trade = {
      id: randomUUID(),
      fromPlayerId: from.id,
      toPlayerId: to.id,
      offeredMoney,
      requestedMoney,
      offeredPropertyIds,
      requestedPropertyIds,
      expectedOwners: Object.fromEntries([
        ...offeredPropertyIds.map((id) => [id, from.id]),
        ...requestedPropertyIds.map((id) => [id, to.id]),
      ]),
      status: 'pending',
      createdAt: Date.now(),
    };
    game.trades.push(trade);
    game.addLog(`${from.name} предлагает сделку игроку ${to.name}.`, 'trade');
    return trade;
  }

  acceptTrade(playerId, tradeId) {
    const { game } = this;
    const trade = game.trades.find((item) => item.id === tradeId && item.status === 'pending');
    invariant(trade && trade.toPlayerId === playerId, 'TRADE_NOT_AVAILABLE', 'Эта сделка недоступна.');
    const from = game.getPlayer(trade.fromPlayerId);
    const to = game.getPlayer(trade.toPlayerId);
    const context = { tradeId, senderId: from?.id, receiverId: to?.id };
    const involvedIds = uniqueIds([...trade.offeredPropertyIds, ...trade.requestedPropertyIds]);
    const before = {
      fromMoney: from?.money,
      toMoney: to?.money,
      fromProperties: from ? [...from.properties] : [],
      toProperties: to ? [...to.properties] : [],
      owners: new Map(involvedIds.map((id) => [id, game.getProperty(id)?.ownerId ?? null])),
    };

    try {
      this.assertTradeAssets(from, trade.offeredMoney, trade.offeredPropertyIds, context);
      this.assertTradeAssets(to, trade.requestedMoney, trade.requestedPropertyIds, context);
      from.money += trade.requestedMoney - trade.offeredMoney;
      to.money += trade.offeredMoney - trade.requestedMoney;
      this.transferProperties(from, to, trade.offeredPropertyIds);
      this.transferProperties(to, from, trade.requestedPropertyIds);
      trade.status = 'accepted';
      const moved = new Set(involvedIds);
      game.trades = game.trades.filter((candidate) => {
        if (candidate.id === trade.id) return false;
        const conflicts = [...candidate.offeredPropertyIds, ...candidate.requestedPropertyIds].some((id) => moved.has(Number(id)));
        if (candidate.status === 'pending' && conflicts) candidate.status = 'cancelled';
        return candidate.status === 'pending' && !conflicts;
      });
      game.addLog(`${to.name} принимает сделку игрока ${from.name}.`, 'trade');
      return game;
    } catch (error) {
      if (from && to) {
        from.money = before.fromMoney;
        to.money = before.toMoney;
        from.properties = before.fromProperties;
        to.properties = before.toProperties;
        for (const [id, ownerId] of before.owners) {
          const property = game.getProperty(id);
          if (property) property.ownerId = ownerId;
        }
      }
      throw error;
    }
  }

  declineTrade(playerId, tradeId) {
    const { game } = this;
    const trade = game.trades.find((item) => item.id === tradeId && item.status === 'pending');
    invariant(trade && [trade.fromPlayerId, trade.toPlayerId].includes(playerId), 'TRADE_NOT_AVAILABLE', 'Эта сделка недоступна.');
    trade.status = 'declined';
    game.trades = game.trades.filter((item) => item.id !== trade.id && item.status === 'pending');
    const actor = game.getPlayer(playerId);
    game.addLog(`${actor.name} отклоняет сделку.`, 'trade');
    return game;
  }

  resolveLanding(player) {
    const { game } = this;
    const cell = game.board[player.position];
    game.addLog(`${player.name} переходит на клетку «${cell.name}».`, 'movement');

    if (PROPERTY_TYPES.has(cell.type)) {
      return this.resolvePropertyLanding(player, cell);
    }
    if (cell.type === 'tax') {
      if (this.requestPayment(player, null, cell.amount, `платёж: ${cell.name}`, { type: 'landingComplete' })) {
        game.addLog(`${player.name} платит ${cell.amount} (${cell.name}).`, 'payment');
        this.setPostLandingPhase(player);
      }
      return;
    }
    if (cell.type === 'chance' || cell.type === 'community') {
      return this.drawCard(player, cell.type);
    }
    if (cell.type === 'goToJail') {
      this.sendToJail(player);
      game.extraTurn = false;
      game.phase = 'awaiting_end';
      return;
    }
    this.setPostLandingPhase(player);
  }

  resolvePropertyLanding(player, cell) {
    const { game } = this;
    const property = game.getProperty(cell.index);
    if (!property.ownerId) {
      game.pendingPurchase = { playerId: player.id, propertyId: property.id };
      game.phase = 'awaiting_purchase';
      return;
    }
    if (property.ownerId === player.id || property.mortgaged) {
      if (property.mortgaged) game.addLog(`«${cell.name}» заложен — аренда не взимается.`);
      this.setPostLandingPhase(player);
      return;
    }

    const owner = game.getPlayer(property.ownerId);
    const rent = calculateRent(game, property.id);
    if (this.requestPayment(player, owner, rent, `аренда: ${cell.name}`, { type: 'landingComplete' })) {
      game.addLog(`${player.name} платит ${owner.name} аренду ${rent} за «${cell.name}».`, 'rent');
      this.setPostLandingPhase(player);
    }
  }

  drawCard(player, deckType) {
    const { game } = this;
    const cards = deckType === 'chance' ? CHANCE_CARDS : COMMUNITY_CARDS;
    const card = cards[Math.floor(this.random() * cards.length)];
    game.lastCard = {
      id: card.id,
      title: card.title,
      description: card.description,
      deck: deckType,
      playerId: player.id,
      sequence: (game.lastCard?.sequence ?? 0) + 1,
    };
    game.addLog(`${player.name}: «${card.title}». ${card.description}`, 'card');
    this.applyCardAction(player, card.action);
  }

  applyCardAction(player, action) {
    const { game } = this;
    switch (action.type) {
      case 'money':
        if (action.amount >= 0) {
          player.money += action.amount;
          this.setPostLandingPhase(player);
        } else if (this.requestPayment(player, null, Math.abs(action.amount), 'платёж по карточке', { type: 'landingComplete' })) {
          this.setPostLandingPhase(player);
        }
        break;
      case 'moveTo':
        this.moveTo(player, action.position, action.collectStart);
        this.resolveLanding(player);
        break;
      case 'moveBy':
        this.moveBy(player, action.offset, false);
        this.resolveLanding(player);
        break;
      case 'goToJail':
        this.sendToJail(player);
        game.extraTurn = false;
        game.phase = 'awaiting_end';
        break;
      case 'jailFree':
        player.jailFreeCards += 1;
        this.setPostLandingPhase(player);
        break;
      case 'nearest': {
        const next = this.findNextCell(player.position, action.propertyType);
        this.moveTo(player, next.index, true);
        this.resolveLanding(player);
        break;
      }
      case 'repairs': {
        const fee = player.properties.reduce((total, id) => {
          const houses = game.getProperty(id).houses;
          return total + (houses === 5 ? action.hotel : houses * action.house);
        }, 0);
        if (fee === 0 || this.requestPayment(player, null, fee, 'ремонт по карточке', { type: 'landingComplete' })) {
          this.setPostLandingPhase(player);
        }
        break;
      }
      case 'collectEach':
        this.collectCardPayments(player, action.amount, game.players
          .filter((other) => !other.bankrupt && other.id !== player.id)
          .map((other) => other.id));
        break;
      case 'payEach': {
        const recipients = game.players.filter((other) => !other.bankrupt && other.id !== player.id);
        const total = recipients.length * action.amount;
        if (this.requestPayment(player, null, total, 'выплата игрокам по карточке', { type: 'payEach', amount: action.amount, recipientIds: recipients.map((item) => item.id) })) {
          recipients.forEach((other) => { other.money += action.amount; });
          this.setPostLandingPhase(player);
        }
        break;
      }
      default:
        this.setPostLandingPhase(player);
    }
  }

  requestPayment(debtor, creditor, amount, reason, afterPayment) {
    const { game } = this;
    if (amount <= debtor.money) {
      debtor.money -= amount;
      if (creditor) creditor.money += amount;
      return true;
    }
    game.pendingDebt = {
      debtorId: debtor.id,
      creditorId: creditor?.id ?? null,
      amount,
      reason,
      afterPayment,
    };
    game.phase = 'awaiting_debt';
    game.addLog(`${debtor.name} должен ${amount}, но на балансе только ${debtor.money}. Нужно продать постройки или заложить имущество.`, 'debt');
    return false;
  }

  runAfterPayment(player, afterPayment) {
    const { game } = this;
    if (afterPayment?.type === 'collectEach') {
      const collector = game.getPlayer(afterPayment.collectorId);
      this.collectCardPayments(collector, afterPayment.amount, afterPayment.remainingPayerIds);
      return;
    }
    if (afterPayment?.type === 'jailMove') {
      player.jail = { inJail: false, turns: 0 };
      this.moveBy(player, afterPayment.steps, false);
      this.resolveLanding(player);
      return;
    }
    if (afterPayment?.type === 'jailRelease') {
      player.jail = { inJail: false, turns: 0 };
      game.phase = 'awaiting_roll';
      return;
    }
    if (afterPayment?.type === 'payEach') {
      afterPayment.recipientIds.forEach((id) => {
        const recipient = game.getPlayer(id);
        if (recipient && !recipient.bankrupt) recipient.money += afterPayment.amount;
      });
    }
    this.setPostLandingPhase(player);
  }

  collectCardPayments(collector, amount, payerIds) {
    const { game } = this;
    for (const [index, playerId] of payerIds.entries()) {
      const payer = game.getPlayer(playerId);
      if (!payer || payer.bankrupt) continue;
      const paid = this.requestPayment(payer, collector, amount, 'выплата игроку по карточке', {
        type: 'collectEach',
        collectorId: collector.id,
        amount,
        remainingPayerIds: payerIds.slice(index + 1),
      });
      if (!paid) return;
      game.addLog(`${payer.name} платит ${collector.name} ${amount} по карточке.`, 'payment');
    }
    this.setPostLandingPhase(collector);
  }

  moveBy(player, steps, awardSalary) {
    const { game } = this;
    const raw = player.position + steps;
    if (awardSalary && raw >= game.board.length) {
      player.money += game.settings.startSalary;
      game.addLog(`${player.name} проходит Старт и получает ${game.settings.startSalary}.`, 'money');
    }
    const direction = steps >= 0 ? 1 : -1;
    const path = [];
    for (let offset = direction; direction > 0 ? offset <= steps : offset >= steps; offset += direction) {
      path.push(((player.position + offset) % game.board.length + game.board.length) % game.board.length);
    }
    player.position = ((raw % game.board.length) + game.board.length) % game.board.length;
    if (game.lastMove?.playerId === player.id) {
      game.lastMove.path.push(...path);
      game.lastMove.to = player.position;
    }
  }

  moveTo(player, position, collectStart) {
    const { game } = this;
    if (collectStart && (position === 0 || position < player.position)) {
      player.money += game.settings.startSalary;
      game.addLog(`${player.name} проходит Старт и получает ${game.settings.startSalary}.`, 'money');
    }
    const path = [];
    let cursor = player.position;
    while (cursor !== position && path.length < game.board.length) {
      cursor = (cursor + 1) % game.board.length;
      path.push(cursor);
    }
    player.position = position;
    if (game.lastMove?.playerId === player.id) {
      game.lastMove.path.push(...path);
      game.lastMove.to = player.position;
    }
  }

  findNextCell(fromPosition, type) {
    const { board } = this.game;
    for (let offset = 1; offset <= board.length; offset += 1) {
      const cell = board[(fromPosition + offset) % board.length];
      if (cell.type === type) return cell;
    }
    return board[fromPosition];
  }

  sendToJail(player) {
    player.position = 10;
    if (this.game.lastMove?.playerId === player.id) {
      this.game.lastMove.path.push(10);
      this.game.lastMove.to = 10;
    }
    player.jail = { inJail: true, turns: 0 };
    this.game.addLog(`${player.name} отправляется в городскую тюрьму.`, 'jail');
  }

  setPostLandingPhase(player) {
    const { game } = this;
    if (game.pendingDebt || game.pendingPurchase || game.status !== 'active') return;
    if (game.extraTurn && !player.jail.inJail) {
      game.phase = 'awaiting_roll';
      game.addLog(`${player.name} получает дополнительный бросок за дубль.`, 'dice');
    } else {
      game.phase = 'awaiting_end';
    }
  }

  advanceTurn() {
    const { game } = this;
    const activePlayers = game.players.filter((player) => !player.bankrupt);
    if (activePlayers.length <= 1) return this.checkWinner();
    let next = game.currentPlayerIndex;
    do {
      next = (next + 1) % game.players.length;
    } while (game.players[next].bankrupt);
    game.currentPlayerIndex = next;
    game.doublesCount = 0;
    game.extraTurn = false;
    game.pendingPurchase = null;
    game.phase = 'awaiting_roll';
    game.addLog(`Ход игрока ${game.currentPlayer.name}.`, 'turn');
  }

  checkWinner() {
    const { game } = this;
    const remaining = game.players.filter((player) => !player.bankrupt);
    if (game.status === 'active' && remaining.length === 1) {
      game.status = 'finished';
      game.phase = 'finished';
      game.winner = { id: remaining[0].id, name: remaining[0].name };
      game.addLog(`${remaining[0].name} победил в «Капиталии»!`, 'winner');
    }
    return game;
  }

  assertCurrentPlayer(playerId) {
    const { game } = this;
    invariant(game.status === 'active', 'GAME_NOT_ACTIVE', 'Матч ещё не начался или уже завершён.');
    invariant(game.currentPlayer?.id === playerId, 'NOT_YOUR_TURN', 'Сейчас ход другого игрока.');
    invariant(!game.currentPlayer.bankrupt, 'PLAYER_BANKRUPT', 'Игрок выбыл из матча.');
    return game.currentPlayer;
  }

  assertManagementTurn(playerId, debtActionsAllowed) {
    const { game } = this;
    if (game.pendingDebt) {
      invariant(debtActionsAllowed && game.pendingDebt.debtorId === playerId, 'DEBT_ACTION_ONLY', 'Сначала погасите долг.');
      invariant(game.status === 'active' && game.phase === 'awaiting_debt', 'MANAGEMENT_NOT_ALLOWED', 'Сейчас нельзя управлять имуществом.');
      const debtor = game.getPlayer(playerId);
      invariant(debtor && !debtor.bankrupt, 'PLAYER_BANKRUPT', 'Игрок выбыл из матча.');
      return;
    }
    this.assertCurrentPlayer(playerId);
    invariant(ACTIVE_PHASES_FOR_MANAGEMENT.has(game.phase), 'MANAGEMENT_NOT_ALLOWED', 'Сейчас нельзя управлять имуществом.');
    if (!debtActionsAllowed) {
      invariant(!game.pendingDebt, 'DEBT_ACTION_ONLY', 'Сначала погасите долг.');
    }
  }

  assertTradeAssets(player, money, propertyIds, context = {}) {
    invariant(player && !player.bankrupt, 'INVALID_TRADE_PLAYER', 'Игрок недоступен для сделки.');
    invariant(player.money >= money, 'TRADE_MONEY_CHANGED', `У игрока ${player.name} недостаточно денег для сделки.`);
    propertyIds.forEach((id) => {
      const property = this.game.getProperty(id);
      const cell = this.game.board[id];
      if (property?.ownerId !== player.id) {
        const details = {
          tradeId: context.tradeId ?? null,
          propertyId: Number(id),
          expectedOwnerId: player.id,
          actualOwnerId: property?.ownerId ?? null,
          senderId: context.senderId ?? null,
          receiverId: context.receiverId ?? null,
          gameId: this.game.id,
          roomId: this.game.roomCode ?? null,
        };
        this.logger.warn?.('[trade:ownership-mismatch]', details);
        invariant(false, 'TRADE_PROPERTY_CHANGED', `Объект «${cell?.name ?? id}» больше не принадлежит игроку.`, details);
      }
      invariant(property.houses === 0 && (cell.type !== 'street' || !groupHasBuildings(this.game, cell.group)), 'DEVELOPED_PROPERTY_TRADE', 'Нельзя передавать объект из группы с постройками.');
    });
  }

  transferProperties(from, to, propertyIds) {
    propertyIds.forEach((id) => {
      const property = this.game.getProperty(id);
      property.ownerId = to.id;
      from.properties = from.properties.filter((propertyId) => propertyId !== id);
      if (!to.properties.includes(id)) to.properties.push(id);
    });
  }
}
